/**
 * Автоисправление. Исправления из замечаний применяются к XML документа:
 *  - в режиме рецензирования: текст — через w:del/w:ins, форматирование —
 *    через w:rPrChange/w:pPrChange/w:sectPrChange, чтобы студент видел
 *    и принимал каждую правку в Word;
 *  - начисто — без пометок.
 * К замечаниям, которые нельзя исправить автоматически, добавляются
 * комментарии Word на полях.
 */

import { paginate } from '../analysis/pages';
import { analyze, type Analysis } from '../check';
import {
  buildModel,
  editableSegments,
  fontsForText,
  wordTocLevel,
  type DocModel,
  type Paragraph,
  type TextSegment,
} from '../model/document';
import { parseRPr } from '../model/props';
import type { StyleSheet } from '../model/styles';
import { Rel, type DocxPackage } from '../ooxml/package';
import { allFonts, tocParagraphProps, tocParagraphs, wrapInBookmark } from '../ooxml/toc';
import {
  ensurePPr,
  ensureRPr,
  getOrCreate,
  LVL_ORDER,
  PARA_RPR_ORDER,
  PPR_ORDER,
  removeChild,
  roundFractions,
  RPR_ORDER,
  SECTPR_ORDER,
  setOrdered,
  snapshotProps,
  STYLE_ORDER,
} from '../ooxml/schema';
import {
  appendChild,
  childElements,
  cloneNode,
  el,
  firstChild,
  insertAfter,
  insertAt,
  insertBefore,
  removeNode,
  textNode,
  XML_DECLARATION,
  type XDocument,
  type XElement,
} from '../ooxml/xml';
import { DEFAULT_PROFILE, type Profile } from '../profiles';
import { ALL_RULES } from '../rules';
import { forProfile, type Finding, type FixAction, type ParaFix, type RunFix } from '../rules/types';
import { normFont, SPECIAL_FONTS } from '../rules/util';

export type FixMode = 'tracked' | 'clean' | 'none';

const SOFT_HYPHEN = String.fromCharCode(0xad);

/** Поля, которые при обновлении обращаются к внешним файлам и адресам. */
const EXTERNAL_FIELDS = new Set(['INCLUDEPICTURE', 'INCLUDETEXT', 'LINK', 'DDE', 'DDEAUTO', 'RD']);

/** Скрытая закладка на тексте, из которого собирается содержание. */
const TOC_RANGE = '_GostTocRange';

export interface FixOptions {
  profile?: Profile;
  /** tracked — в режиме рецензирования, clean — начисто, none — только комментарии. */
  mode: FixMode;
  /** Добавить комментарии Word к замечаниям, которые не исправлены автоматически. */
  comments: boolean;
  /** Идентификаторы замечаний или правил, которые не нужно исправлять. */
  exclude?: string[];
  author?: string;
  /** Дата правок в ISO 8601; по умолчанию — текущий момент. */
  date?: string;
}

export interface FixResult {
  bytes: Uint8Array;
  fixed: number;
  commented: number;
  /** Исправления, которые не удалось применить (например, текст внутри поля). */
  failed: number;
}

const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
const COMMENTS_REL = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/comments';
const COMMENTS_CT = 'application/vnd.openxmlformats-officedocument.wordprocessingml.comments+xml';
const R_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const HF = {
  header: { rel: `${R_NS}/header`, ct: 'application/vnd.openxmlformats-officedocument.wordprocessingml.header+xml', root: 'w:hdr', ref: 'w:headerReference' },
  footer: { rel: `${R_NS}/footer`, ct: 'application/vnd.openxmlformats-officedocument.wordprocessingml.footer+xml', root: 'w:ftr', ref: 'w:footerReference' },
} as const;

/** Элементы w:settings, которые по схеме идут после w:updateFields. */
const AFTER_UPDATE_FIELDS = [
  'w:hdrShapeDefaults', 'w:footnotePr', 'w:endnotePr', 'w:compat', 'w:docVars', 'w:rsids', 'm:mathPr',
  'w:attachedSchema', 'w:themeFontLang', 'w:clrSchemeMapping', 'w:doNotIncludeSubdocsInStats',
  'w:doNotAutoCompressPictures', 'w:forceUpgrade', 'w:captions', 'w:readModeInkLockDown', 'w:smartTagType',
  'sl:schemaLibrary', 'w:shapeDefaults', 'w:doNotEmbedSmartTags', 'w:decimalSymbol', 'w:listSeparator',
];

function maxNumericId(root: XElement): number {
  let max = 0;
  const walk = (e: XElement): void => {
    const v = e.attrs['w:id'];
    if (v !== undefined) {
      const n = parseInt(v, 10);
      if (Number.isFinite(n) && n > max) max = n;
    }
    for (const c of e.children) if (c.type === 'element') walk(c);
  };
  walk(root);
  return max;
}

/** Фрагменты текста абзаца, которые видны читателю (без удалённых). */
function visibleRuns(e: XElement, out: XElement[] = []): XElement[] {
  for (const c of e.children) {
    if (c.type !== 'element') continue;
    switch (c.name) {
      case 'w:r':
        out.push(c);
        break;
      case 'w:hyperlink':
      case 'w:ins':
      case 'w:moveTo':
      case 'w:smartTag':
      case 'w:customXml':
      case 'w:fldSimple':
      case 'w:dir':
      case 'w:bdo':
        visibleRuns(c, out);
        break;
      case 'w:sdt': {
        const content = firstChild(c, 'w:sdtContent');
        if (content) visibleRuns(content, out);
        break;
      }
    }
  }
  return out;
}

function runText(r: XElement): string {
  let s = '';
  for (const c of r.children) {
    if (c.type === 'element' && c.name === 'w:t') for (const t of c.children) if (t.type === 'text') s += t.value;
  }
  return s;
}

function relativeTarget(fromPart: string, toPart: string): string {
  const dir = fromPart.includes('/') ? fromPart.slice(0, fromPart.lastIndexOf('/') + 1) : '';
  return toPart.startsWith(dir) ? toPart.slice(dir.length) : '/' + toPart;
}

/** Ссылки на колонтитулы идут первыми в w:sectPr: сначала верхние, затем нижние. */
function insertReference(sectPr: XElement, ref: XElement): void {
  const before = ref.name === 'w:headerReference' ? ['w:headerReference'] : ['w:headerReference', 'w:footerReference'];
  let idx = 0;
  sectPr.children.forEach((c, i) => {
    if (c.type === 'element' && before.includes(c.name)) idx = i + 1;
  });
  insertAt(sectPr, ref, idx);
}

class Editor {
  private nextId: number;
  /** Фрагменты, вставленные нами в режиме рецензирования: их форматируем без пометок. */
  private readonly created = new WeakSet<XElement>();
  private readonly textRanges = new Map<number, Array<[number, number]>>();
  /** Вставленные содержания: после всех правок их пункты пересобираются по готовому документу (finishToc). */
  private readonly tocs: Array<{ action: Extract<FixAction, { kind: 'toc' }>; entries: XElement[]; levelIndent: number }> = [];
  /** Стили, созданные исправлением: их дальнейшие изменения не отмечаются как правки. */
  private readonly createdStyles = new Set<string>();

  constructor(
    private readonly a: Analysis,
    private readonly mode: 'tracked' | 'clean',
    private readonly author: string,
    private readonly date: string,
  ) {
    this.nextId = maxNumericId(a.pkg.getXml(a.model.mainPart)!.root) + 1;
  }

  allocId(): string {
    return String(this.nextId++);
  }

  private rev(): Record<string, string> {
    return { 'w:id': this.allocId(), 'w:author': this.author, 'w:date': this.date };
  }

  apply(action: FixAction): boolean {
    const paras = this.a.model.paragraphs;
    switch (action.kind) {
      case 'text': {
        const p = paras[action.paragraph];
        return !!p && this.text(p, action);
      }
      case 'runs': {
        const p = paras[action.paragraph];
        return !!p && this.runs(p, action.set);
      }
      case 'para': {
        const p = paras[action.paragraph];
        return !!p && this.para(p.el, action.set);
      }
      case 'section':
        return this.section(action);
      case 'titlePage':
        return this.titlePage(action.section);
      case 'hfAlign': {
        const hf = this.a.model.headersFooters.get(action.part);
        const p = hf?.paragraphs.find((x) => x.index === action.index);
        if (!p) return false;
        this.para(p.el, { jc: action.jc, indFirst: 0, indLeft: 0 });
        this.a.pkg.touch(action.part);
        return true;
      }
      case 'updateFields':
        return this.updateFields();
      case 'meta':
        return this.clearMeta(action.clear);
      case 'pageNumbers':
        return this.pageNumbers(action);
      case 'bullet':
        return this.bullet(action);
      case 'toc':
        return this.toc(action);
    }
  }

  private text(p: Paragraph, a: Extract<FixAction, { kind: 'text' }>): boolean {
    if (p.text.slice(a.start, a.end) !== a.expect) return false;
    const ranges = this.textRanges.get(p.index) ?? [];
    const overlaps = ranges.some(
      ([s, e]) => (a.start < e && s < a.end) || (a.start === a.end && s < a.start && a.start < e) || (s === e && a.start < s && s < a.end),
    );
    if (overlaps) return false;
    // Совпадающие начало и конец замены не трогаем: «␣␣» → «␣» — это удаление одного пробела, без лишних пометок.
    let { start, end, replace } = a;
    while (start < end && replace && p.text[start] === replace[0]) [start, replace] = [start + 1, replace.slice(1)];
    while (start < end && replace && p.text[end - 1] === replace[replace.length - 1]) [end, replace] = [end - 1, replace.slice(0, -1)];
    if (start === end && !replace) return true;
    const segs = editableSegments(p, start, end);
    // Замена встаёт в первый текстовый сегмент, остальные только удаляются.
    const first = segs?.find((s) => s.node);
    if (!segs || (replace && !first) || segs.some((s) => !(s.node?.parent ?? s.tab)?.parent?.parent)) return false;
    // С конца к началу: правка сегмента не сдвигает узлы перед ним.
    for (const seg of [...segs].reverse()) {
      const from = Math.max(start, seg.start) - seg.start;
      const to = Math.min(end, seg.end) - seg.start;
      this.editSegment(seg, from, to, seg === first ? replace : '', !!a.silent);
    }
    ranges.push([a.start, a.end]);
    this.textRanges.set(p.index, ranges);
    return true;
  }

  /** Правка одного сегмента: в тексте w:t — замена from–to на replace, табуляция удаляется целиком. */
  private editSegment(seg: TextSegment, from: number, to: number, replace: string, silent: boolean): void {
    const holder = (seg.node?.parent ?? seg.tab)!;
    const r = holder.parent!;
    if (seg.node) holder.attrs['xml:space'] = 'preserve';
    const value = seg.node?.value ?? '';

    if (this.mode === 'clean' || silent) {
      if (seg.node) seg.node.value = value.slice(0, from) + replace + value.slice(to);
      else removeNode(holder);
      return;
    }
    const rPr = firstChild(r, 'w:rPr');
    const makeRun = (children: XElement[]): XElement => {
      const run = el('w:r');
      if (rPr) {
        const copy = cloneNode(rPr);
        removeChild(copy, 'w:rPrChange');
        appendChild(run, copy);
      }
      for (const c of children) appendChild(run, c);
      return run;
    };
    // Узлы после w:t (или табуляции) переносим в «хвостовой» фрагмент, чтобы сохранить порядок.
    const tail = r.children.slice(r.children.indexOf(holder) + 1);
    const inserted: XElement[] = [];
    let after = '';
    if (seg.node) {
      const old = value.slice(from, to);
      after = value.slice(to);
      seg.node.value = value.slice(0, from);
      if (old) inserted.push(el('w:del', this.rev(), [makeRun([el('w:delText', { 'xml:space': 'preserve' }, [textNode(old)])])]));
      if (replace) {
        const insRun = makeRun([el('w:t', { 'xml:space': 'preserve' }, [textNode(replace)])]);
        this.created.add(insRun);
        inserted.push(el('w:ins', this.rev(), [insRun]));
      }
    } else {
      // Удалённая табуляция в режиме рецензирования — w:tab внутри w:del, как её записывает Word.
      removeNode(holder);
      inserted.push(el('w:del', this.rev(), [makeRun([holder])]));
    }
    if (after || tail.length) {
      const afterRun = makeRun(after ? [el('w:t', { 'xml:space': 'preserve' }, [textNode(after)])] : []);
      for (const n of tail) appendChild(afterRun, n);
      inserted.push(afterRun);
    }
    let ref: XElement = r;
    for (const n of inserted) ref = insertAfter(ref, n);
  }

  private runs(p: Paragraph, set: RunFix): boolean {
    const styles = this.a.model.styles;
    let changed = false;
    for (const r of visibleRuns(p.el)) {
      const text = runText(r);
      if (!/[\p{L}\d]/u.test(text)) continue;
      const rPrEl = firstChild(r, 'w:rPr');
      const rStyle = rPrEl ? firstChild(rPrEl, 'w:rStyle')?.attrs['w:val'] : undefined;
      const eff = styles.resolveRun(p.styleId, rStyle, parseRPr(rPrEl)).props;
      const ops: Array<(rPr: XElement) => void> = [];
      if (set.font) {
        const target = normFont(set.font);
        const fonts = fontsForText(styles, eff, text);
        if (fonts.some((f) => normFont(f) !== target && !SPECIAL_FONTS.test(normFont(f)))) {
          ops.push((rp) => {
            const f = getOrCreate(rp, 'w:rFonts', RPR_ORDER);
            // Атрибуты темы важнее явного имени шрифта — их нужно убрать.
            for (const k of ['w:asciiTheme', 'w:hAnsiTheme', 'w:eastAsiaTheme', 'w:cstheme']) delete f.attrs[k];
            f.attrs['w:ascii'] = set.font!;
            f.attrs['w:hAnsi'] = set.font!;
            // Восточноазиатский слот — для кириллицы с пометкой w:hint="eastAsia".
            f.attrs['w:eastAsia'] = set.font!;
            f.attrs['w:cs'] = set.font!;
          });
        }
      }
      if (set.sz !== undefined && eff.vertAlign !== 'superscript' && eff.vertAlign !== 'subscript' && (eff.sz ?? styles.defaultsR.sz ?? 20) !== set.sz) {
        ops.push((rp) => {
          setOrdered(rp, el('w:sz', { 'w:val': String(set.sz) }), RPR_ORDER);
          setOrdered(rp, el('w:szCs', { 'w:val': String(set.sz) }), RPR_ORDER);
        });
      }
      if (set.color && eff.color && eff.color !== 'auto' && eff.color !== '000000') {
        ops.push((rp) => setOrdered(rp, el('w:color', { 'w:val': 'auto' }), RPR_ORDER));
      }
      if (set.bold !== undefined && !!eff.b !== set.bold) {
        ops.push((rp) => {
          setOrdered(rp, set.bold ? el('w:b') : el('w:b', { 'w:val': '0' }), RPR_ORDER);
          setOrdered(rp, set.bold ? el('w:bCs') : el('w:bCs', { 'w:val': '0' }), RPR_ORDER);
        });
      }
      if (set.caps !== undefined && !!eff.caps !== set.caps) {
        ops.push((rp) => setOrdered(rp, set.caps ? el('w:caps') : el('w:caps', { 'w:val': '0' }), RPR_ORDER));
      }
      if (set.underline === false && eff.u && eff.u !== 'none') {
        ops.push((rp) => setOrdered(rp, el('w:u', { 'w:val': 'none' }), RPR_ORDER));
      }
      if (
        set.highlight === false &&
        ((eff.highlight && eff.highlight !== 'none') || (eff.shading && eff.shading !== 'auto' && eff.shading !== 'FFFFFF'))
      ) {
        ops.push((rp) => {
          removeChild(rp, 'w:highlight');
          setOrdered(rp, el('w:shd', { 'w:val': 'clear', 'w:color': 'auto', 'w:fill': 'auto' }), RPR_ORDER);
        });
      }
      if (!ops.length) continue;
      const rPr = ensureRPr(r);
      if (this.mode === 'tracked' && !this.created.has(r) && !firstChild(rPr, 'w:rPrChange')) {
        setOrdered(rPr, el('w:rPrChange', this.rev(), [snapshotProps(rPr, 'w:rPr', ['w:rPrChange'])]), RPR_ORDER);
      }
      for (const op of ops) op(rPr);
      roundFractions(rPr);
      changed = true;
    }
    return changed;
  }

  private para(pEl: XElement, set: ParaFix): boolean {
    const pPr = ensurePPr(pEl);
    if (this.mode === 'tracked' && !firstChild(pPr, 'w:pPrChange')) {
      setOrdered(pPr, el('w:pPrChange', this.rev(), [snapshotProps(pPr, 'w:pPr', ['w:rPr', 'w:sectPr', 'w:pPrChange'])]), PPR_ORDER);
    }
    if (set.jc) setOrdered(pPr, el('w:jc', { 'w:val': set.jc === 'justify' ? 'both' : set.jc }), PPR_ORDER);
    if (set.indFirst !== undefined || set.indLeft !== undefined) {
      const ind = getOrCreate(pPr, 'w:ind', PPR_ORDER);
      if (set.indFirst !== undefined) {
        for (const k of ['w:firstLine', 'w:hanging', 'w:firstLineChars', 'w:hangingChars']) delete ind.attrs[k];
        if (set.indFirst >= 0) ind.attrs['w:firstLine'] = String(set.indFirst);
        else ind.attrs['w:hanging'] = String(-set.indFirst);
      }
      if (set.indLeft !== undefined) {
        ind.attrs[ind.attrs['w:start'] !== undefined ? 'w:start' : 'w:left'] = String(set.indLeft);
        delete ind.attrs['w:leftChars'];
        delete ind.attrs['w:startChars'];
      }
    }
    if (set.spacingBefore !== undefined || set.spacingAfter !== undefined || set.line !== undefined) {
      const sp = getOrCreate(pPr, 'w:spacing', PPR_ORDER);
      if (set.spacingBefore !== undefined) {
        sp.attrs['w:before'] = String(set.spacingBefore);
        delete sp.attrs['w:beforeLines'];
        delete sp.attrs['w:beforeAutospacing'];
      }
      if (set.spacingAfter !== undefined) {
        sp.attrs['w:after'] = String(set.spacingAfter);
        delete sp.attrs['w:afterLines'];
        delete sp.attrs['w:afterAutospacing'];
      }
      if (set.line !== undefined) {
        sp.attrs['w:line'] = String(set.line);
        sp.attrs['w:lineRule'] = set.lineRule ?? 'auto';
      }
    }
    if (set.keepNext) setOrdered(pPr, el('w:keepNext'), PPR_ORDER);
    if (set.outlineLvl !== undefined) setOrdered(pPr, el('w:outlineLvl', { 'w:val': String(set.outlineLvl) }), PPR_ORDER);
    if (set.pageBreakBefore) setOrdered(pPr, el('w:pageBreakBefore'), PPR_ORDER);
    if (set.removeNumbering) {
      setOrdered(pPr, el('w:numPr', {}, [el('w:ilvl', { 'w:val': '0' }), el('w:numId', { 'w:val': '0' })]), PPR_ORDER);
    }
    roundFractions(pPr);
    return true;
  }

  private trackSection(sp: XElement): void {
    if (this.mode === 'tracked' && !firstChild(sp, 'w:sectPrChange')) {
      const snap = snapshotProps(sp, 'w:sectPr', ['w:headerReference', 'w:footerReference', 'w:sectPrChange']);
      setOrdered(sp, el('w:sectPrChange', this.rev(), [snap]), SECTPR_ORDER);
    }
  }

  private section(a: Extract<FixAction, { kind: 'section' }>): boolean {
    const sec = this.a.model.sections[a.section];
    if (!sec) return false;
    const sp = sec.el;
    this.trackSection(sp);
    if (a.widthTw !== undefined || a.heightTw !== undefined) {
      const pg = getOrCreate(sp, 'w:pgSz', SECTPR_ORDER);
      if (a.widthTw !== undefined) pg.attrs['w:w'] = String(a.widthTw);
      if (a.heightTw !== undefined) pg.attrs['w:h'] = String(a.heightTw);
      if (a.widthTw !== undefined && a.heightTw !== undefined) {
        if (a.widthTw > a.heightTw) pg.attrs['w:orient'] = 'landscape';
        else delete pg.attrs['w:orient'];
      }
      delete pg.attrs['w:code'];
    }
    if (a.margins) {
      const pm = getOrCreate(sp, 'w:pgMar', SECTPR_ORDER);
      for (const [k, v] of Object.entries(a.margins)) {
        if (v === undefined) continue;
        const name = k === 'left' && pm.attrs['w:start'] !== undefined ? 'w:start' : k === 'right' && pm.attrs['w:end'] !== undefined ? 'w:end' : `w:${k}`;
        pm.attrs[name] = String(v);
      }
      // Все атрибуты w:pgMar обязательны по схеме.
      const defaults: Record<string, string> = { 'w:top': '1134', 'w:right': '850', 'w:bottom': '1134', 'w:left': '1701', 'w:header': '708', 'w:footer': '708', 'w:gutter': '0' };
      for (const [k, v] of Object.entries(defaults)) {
        const alt = k === 'w:left' ? 'w:start' : k === 'w:right' ? 'w:end' : undefined;
        if (pm.attrs[k] === undefined && !(alt && pm.attrs[alt] !== undefined)) pm.attrs[k] = v;
      }
    }
    if (a.clearPageRestart) {
      const pn = firstChild(sp, 'w:pgNumType');
      if (pn) {
        delete pn.attrs['w:start'];
        if (pn.attrs['w:fmt'] && pn.attrs['w:fmt'] !== 'decimal') delete pn.attrs['w:fmt'];
      }
    }
    roundFractions(sp);
    return true;
  }

  private titlePage(section: number): boolean {
    const sec = this.a.model.sections[section];
    if (!sec) return false;
    if (firstChild(sec.el, 'w:titlePg')) return true;
    this.trackSection(sec.el);
    setOrdered(sec.el, el('w:titlePg'), SECTPR_ORDER);
    return true;
  }

  private updateFields(): boolean {
    // Обновление при открытии затронет и поля со ссылками на внешние файлы и адреса: Word покажет
    // предупреждение безопасности и обратится к сети. В таких документах поля обновляет сам студент.
    const model = this.a.model;
    const paras = [...model.paragraphs, ...[...model.headersFooters.values()].flatMap((hf) => hf.paragraphs)];
    if (paras.some((p) => p.fields.some((f) => EXTERNAL_FIELDS.has(f.type)))) return false;
    const pkg = this.a.pkg;
    const path = pkg.relatedPart(this.a.model.mainPart, Rel.settings);
    const doc = path ? pkg.getXml(path) : undefined;
    if (!path || !doc) return false;
    if (firstChild(doc.root, 'w:updateFields')) return true;
    const node = el('w:updateFields', { 'w:val': 'true' });
    const before = childElements(doc.root).find((c) => AFTER_UPDATE_FIELDS.includes(c.name));
    if (before) insertBefore(before, node);
    else appendChild(doc.root, node);
    pkg.touch(path);
    return true;
  }

  private clearMeta(fields: Array<'creator' | 'lastModifiedBy'>): boolean {
    const pkg = this.a.pkg;
    const rel = pkg.rels('').find((r) => r.type.endsWith(Rel.coreProps));
    const path = rel?.target ?? 'docProps/core.xml';
    const doc = pkg.getXml(path);
    if (!doc) return false;
    for (const f of fields) {
      const e = firstChild(doc.root, f === 'creator' ? 'dc:creator' : 'cp:lastModifiedBy');
      if (e) e.children = [];
    }
    pkg.touch(path);
    return true;
  }

  /** Поле PAGE: в собственный колонтитул раздела или в новый, который наследуют следующие разделы. */
  private pageNumbers(a: Extract<FixAction, { kind: 'pageNumbers' }>): boolean {
    const pkg = this.a.pkg;
    const main = this.a.model.mainPart;
    const kind = a.position === 'top' ? 'header' : 'footer';
    const run = (child: XElement): XElement => {
      const props: XElement[] = [];
      if (a.font) props.push(el('w:rFonts', { 'w:ascii': a.font, 'w:hAnsi': a.font, 'w:cs': a.font }));
      if (a.sz) props.push(el('w:sz', { 'w:val': String(a.sz) }), el('w:szCs', { 'w:val': String(a.sz) }));
      return el('w:r', {}, props.length ? [el('w:rPr', {}, props), child] : [child]);
    };
    const paragraph = (): XElement => {
      const runs = [
        run(el('w:fldChar', { 'w:fldCharType': 'begin' })),
        run(el('w:instrText', { 'xml:space': 'preserve' }, [textNode(' PAGE ')])),
        run(el('w:fldChar', { 'w:fldCharType': 'separate' })),
        run(el('w:t', {}, [textNode('1')])),
        run(el('w:fldChar', { 'w:fldCharType': 'end' })),
      ];
      const pPr = el('w:pPr', {}, [el('w:jc', { 'w:val': a.align })]);
      if (this.mode === 'clean') return el('w:p', {}, [pPr, ...runs]);
      appendChild(pPr, el('w:rPr', {}, [el('w:ins', this.rev())]));
      return el('w:p', {}, [pPr, el('w:ins', this.rev(), runs)]);
    };
    const root = pkg.getXml(main)?.root;
    if (root && root.attrs['xmlns:r'] === undefined) root.attrs['xmlns:r'] = R_NS;
    const touched = new Set<string>();
    let created: string | undefined;
    let changed = false;
    for (const sec of this.a.model.sections) {
      const ref = childElements(sec.el, HF[kind].ref).find((r) => (r.attrs['w:type'] ?? 'default') === 'default');
      if (ref) {
        const rel = pkg.relById(main, ref.attrs['r:id'] ?? '');
        const doc = rel && !rel.external ? pkg.getXml(rel.target) : undefined;
        if (!rel || !doc || touched.has(rel.target)) continue;
        appendChild(doc.root, paragraph());
        pkg.touch(rel.target);
        touched.add(rel.target);
        changed = true;
      } else if (sec.index === 0) {
        created ??= this.createHeaderFooter(kind, paragraph());
        insertReference(sec.el, el(HF[kind].ref, { 'w:type': 'default', 'r:id': created }));
        changed = true;
      }
    }
    return changed;
  }

  private createHeaderFooter(kind: 'header' | 'footer', p: XElement): string {
    const pkg = this.a.pkg;
    const main = this.a.model.mainPart;
    const dir = main.includes('/') ? main.slice(0, main.lastIndexOf('/') + 1) : '';
    let path = `${dir}${kind}1.xml`;
    for (let n = 2; pkg.has(path); n++) path = `${dir}${kind}${n}.xml`;
    pkg.putXml(path, { prolog: XML_DECLARATION, root: el(HF[kind].root, { 'xmlns:w': W_NS, 'xmlns:r': R_NS }, [p]), epilog: '' });
    pkg.setContentTypeOverride(path, HF[kind].ct);
    return pkg.addRelationship(main, HF[kind].rel, relativeTarget(main, path));
  }

  /** Маркер уровня списка в numbering.xml — меняется во всех списках этого вида сразу. */
  private bullet(a: Extract<FixAction, { kind: 'bullet' }>): boolean {
    const pkg = this.a.pkg;
    const path = pkg.relatedPart(this.a.model.mainPart, Rel.numbering);
    const doc = path ? pkg.getXml(path) : undefined;
    if (!path || !doc) return false;
    const num = childElements(doc.root, 'w:num').find((n) => n.attrs['w:numId'] === String(a.numId));
    if (!num) return false;
    const ilvl = String(a.ilvl);
    // Переопределение уровня в самом списке важнее общего определения.
    const override = childElements(num, 'w:lvlOverride').find((o) => (o.attrs['w:ilvl'] ?? '0') === ilvl);
    let lvl = override ? firstChild(override, 'w:lvl') : undefined;
    if (!lvl) {
      const absId = firstChild(num, 'w:abstractNumId')?.attrs['w:val'];
      const abs = childElements(doc.root, 'w:abstractNum').find((x) => x.attrs['w:abstractNumId'] === absId);
      lvl = abs ? childElements(abs, 'w:lvl').find((l) => (l.attrs['w:ilvl'] ?? '0') === ilvl) : undefined;
    }
    if (!lvl) return false;
    setOrdered(lvl, el('w:lvlText', { 'w:val': a.text }), LVL_ORDER);
    removeChild(lvl, 'w:lvlPicBulletId');
    const rPr = getOrCreate(lvl, 'w:rPr', LVL_ORDER);
    setOrdered(rPr, el('w:rFonts', { 'w:ascii': a.font, 'w:hAnsi': a.font, 'w:cs': a.font, 'w:hint': 'default' }), RPR_ORDER);
    pkg.touch(path);
    return true;
  }

  /** Автоматическое содержание: закладки на заголовках, поле TOC с гиперссылками, обновление при открытии. */
  private toc(a: Extract<FixAction, { kind: 'toc' }>): boolean {
    const paras = this.a.model.paragraphs;
    const anchor = a.replace ? paras[a.replace.first] : paras[a.after ?? a.before ?? -1];
    const parent = anchor?.el.parent;
    if (!anchor || !parent || anchor.cell || anchor.textBox) return false;
    const olds = a.replace ? paras.slice(a.replace.first, a.replace.last + 1) : [];
    if (olds.some((p) => p.el.parent !== parent)) return false;

    // Закладки на заголовках: существующие _Toc… переиспользуем.
    const used = new Set(paras.flatMap((p) => p.bookmarks));
    let serial = 0;
    const entries = [];
    for (const e of a.entries) {
      const p = paras[e.paragraph];
      if (!p || p.cell || p.textBox) return false;
      let name = p.bookmarks.find((b) => /^_Toc\d+$/.test(b));
      if (!name) {
        do name = `_Toc${201600000 + ++serial}`;
        while (used.has(name));
        used.add(name);
        wrapInBookmark(p.el, name, this.allocId());
        p.bookmarks.push(name);
      }
      entries.push({ text: this.tocEntryText(p) || e.text, level: e.level, bookmark: name, page: e.page });
    }

    const levelIndent = 284;
    const { paragraphs } = tocParagraphs(entries, {
      depth: a.depth,
      styleIds: this.tocStyles(a, levelIndent),
      tabPos: a.tabPos,
      levelIndent,
      font: a.font,
      sz: a.sz,
      line: a.line,
      range: TOC_RANGE,
      revision: this.mode === 'tracked' ? () => this.rev() : undefined,
    });
    this.tocs.push({ action: a, entries: [...paragraphs], levelIndent });
    if (a.title) paragraphs.unshift(this.tocTitle(a, a.title));

    let at = parent.children.indexOf(anchor.el) + (a.after !== undefined ? 1 : 0);
    for (const p of paragraphs) insertAt(parent, p, at++);
    for (const old of olds) {
      if (this.mode === 'clean') parent.children.splice(parent.children.indexOf(old.el), 1);
      else this.trackDeleteParagraph(old.el);
    }
    this.markTocRange(paragraphs[paragraphs.length - 1]!);
    this.updateFields();
    return true;
  }

  /**
   * Закладка на тексте после содержания: из неё поле берёт заголовки (\b), поэтому строки титульного листа
   * в стиле заголовка и само «СОДЕРЖАНИЕ» в пункты не попадут. Начало — в первом абзаце после содержания,
   * конец — в последнем абзаце документа.
   */
  private markTocRange(last: XElement): void {
    const body = firstChild(this.a.pkg.getXml(this.a.model.mainPart)!.root, 'w:body')!;
    // Документ уже исправляли — прежнюю закладку убираем.
    const stale = new Set<string>();
    const drop = (e: XElement, name: 'w:bookmarkStart' | 'w:bookmarkEnd'): void => {
      for (const c of [...e.children]) {
        if (c.type !== 'element') continue;
        if (c.name === name && (name === 'w:bookmarkStart' ? c.attrs['w:name'] === TOC_RANGE : stale.has(c.attrs['w:id'] ?? ''))) {
          stale.add(c.attrs['w:id'] ?? '');
          removeNode(c);
        } else drop(c, name);
      }
    };
    drop(body, 'w:bookmarkStart');
    if (stale.size) drop(body, 'w:bookmarkEnd');

    const id = this.allocId();
    const start = el('w:bookmarkStart', { 'w:id': id, 'w:name': TOC_RANGE });
    const next = last.parent?.children[last.parent.children.indexOf(last) + 1];
    if (next?.type === 'element' && next.name === 'w:p') insertAt(next, start, firstChild(next, 'w:pPr') ? 1 : 0);
    else insertAfter(last, start);
    const end = el('w:bookmarkEnd', { 'w:id': id });
    const blocks = childElements(body).filter((c) => c.name !== 'w:sectPr');
    const tail = blocks[blocks.length - 1];
    if (tail?.name === 'w:p') appendChild(tail, end);
    else if (tail) insertAfter(tail, end);
    else appendChild(body, end);
  }

  /**
   * Пункты вставленного содержания по готовому документу. Обновляя поле, Word возьмёт абзацы по уровням
   * структуры (у встроенных стилей «Заголовок N» — всегда N), поэтому и мы берём их так же, а номера страниц
   * считаем по вёрстке после всех исправлений: полуторный интервал и новые разрывы заметно удлиняют работу.
   */
  finishToc(): void {
    for (const t of this.tocs) {
      const first = t.entries[0];
      const parent = first?.parent;
      if (!first || !parent) continue;
      let model: DocModel;
      try {
        model = buildModel(this.a.pkg);
      } catch {
        continue;
      }
      const own = new Set(t.entries);
      const levelOf = (p: Paragraph): number | undefined => wordTocLevel(model, p);
      // Заголовки, найденные проверкой, должны остаться в содержании: у «Заголовка 3» поле берёт и третий уровень.
      const wanted = new Set(t.action.entries.map((e) => this.a.model.paragraphs[e.paragraph]?.el));
      let depth = t.action.depth;
      for (const p of model.paragraphs) {
        const level = wanted.has(p.el) ? levelOf(p) : undefined;
        if (level !== undefined && level > depth && level <= 3) depth = level;
      }
      // Как и поле с ключом \b, берём только заголовки после содержания.
      const tocEnd = Math.max(...model.paragraphs.filter((p) => own.has(p.el)).map((p) => p.index));
      const picked = model.paragraphs.filter(
        (p) => p.index > tocEnd && !p.inToc && !p.textBox && /[\p{L}\d]/u.test(p.text) && (levelOf(p) ?? 10) <= depth,
      );
      if (!picked.length) continue;

      const used = new Set(model.paragraphs.flatMap((p) => p.bookmarks));
      let serial = used.size;
      const pageOf = paginate(model, { estimateOnly: true }).pageOf;
      const entries = picked.map((p) => {
        let name = p.bookmarks.find((b) => /^_Toc\d+$/.test(b));
        if (!name) {
          do name = `_Toc${201600000 + ++serial}`;
          while (used.has(name));
          used.add(name);
          wrapInBookmark(p.el, name, this.allocId());
        }
        return { text: this.tocEntryText(p) || p.text.trim(), level: levelOf(p)!, bookmark: name, page: pageOf[p.index] ?? 1 };
      });
      const { paragraphs, pageNodes } = tocParagraphs(entries, {
        depth,
        styleIds: this.tocStyles({ ...t.action, depth }, t.levelIndent, model.styles),
        tabPos: t.action.tabPos,
        levelIndent: t.levelIndent,
        font: t.action.font,
        sz: t.action.sz,
        line: t.action.line,
        range: TOC_RANGE,
        revision: this.mode === 'tracked' ? () => this.rev() : undefined,
      });
      const at = parent.children.indexOf(first);
      for (const old of t.entries) removeNode(old);
      paragraphs.forEach((p, k) => insertAt(parent, p, at + k));

      // Содержание другой длины сдвигает страницы за ним: номера — по окончательной вёрстке.
      model = buildModel(this.a.pkg);
      const finalPages = paginate(model, { estimateOnly: true }).pageOf;
      const pageByEl = new Map(model.paragraphs.map((p) => [p.el, finalPages[p.index]]));
      picked.forEach((p, k) => (pageNodes[k]!.value = String(pageByEl.get(p.el) ?? entries[k]!.page)));
      this.a.pkg.touch(this.a.model.mainPart);
    }
  }

  /**
   * Текст пункта таким, каким его запишет Word при обновлении поля: заголовок уже после исправлений
   * (удалённое не входит, вставленное входит), прописными — если у заголовка прямое оформление
   * «все прописные» (его Word переносит в пункт), с номером автонумерации и без точки в конце.
   */
  private tocEntryText(p: Paragraph): string {
    let text = '';
    let lettered = 0;
    let caps = 0;
    for (const r of visibleRuns(p.el)) {
      let s = '';
      for (const c of r.children) {
        if (c.type !== 'element') continue;
        if (c.name === 'w:t') for (const t of c.children) s += t.type === 'text' ? t.value : '';
        else if (c.name === 'w:tab' || c.name === 'w:br' || c.name === 'w:cr') s += ' ';
        else if (c.name === 'w:noBreakHyphen') s += '-';
      }
      if (/\p{L}/u.test(s)) {
        lettered++;
        const rPr = firstChild(r, 'w:rPr');
        const flag = rPr && firstChild(rPr, 'w:caps');
        if (flag && !['0', 'false', 'off'].includes(flag.attrs['w:val'] ?? '')) caps++;
      }
      text += s;
    }
    if (lettered && caps === lettered) text = text.toUpperCase();
    const auto = p.list && !p.list.bullet ? `${p.list.label.trim()} ` : '';
    return (auto + text.split(SOFT_HYPHEN).join(''))
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/(?<!\.)[.:;]+$/, '')
      .trim();
  }

  private tocTitle(a: Extract<FixAction, { kind: 'toc' }>, title: { text: string; bold: boolean }): XElement {
    const rPr: XElement[] = [];
    if (a.font) rPr.push(allFonts(a.font));
    if (title.bold) rPr.push(el('w:b'), el('w:bCs'));
    if (a.sz) rPr.push(el('w:sz', { 'w:val': String(a.sz) }), el('w:szCs', { 'w:val': String(a.sz) }));
    const run = el('w:r', {}, [el('w:rPr', {}, rPr), el('w:t', { 'xml:space': 'preserve' }, [textNode(title.text)])]);
    const pPr = el('w:pPr', {}, [
      el('w:keepNext'),
      el('w:pageBreakBefore'),
      el('w:spacing', { 'w:before': '0', 'w:after': '0' }),
      el('w:ind', { 'w:left': '0', 'w:firstLine': '0' }),
      el('w:jc', { 'w:val': 'center' }),
    ]);
    if (this.mode === 'tracked') {
      appendChild(pPr, el('w:rPr', {}, [el('w:ins', this.rev())]));
      return el('w:p', {}, [pPr, el('w:ins', this.rev(), [run])]);
    }
    return el('w:p', {}, [pPr, run]);
  }

  /**
   * Стили «Оглавление 1…N». Обновляя поле, Word оставляет пунктам только стиль, поэтому шрифт, кегль,
   * интервалы и отступы пишем в стиль: в существующий (в режиме рецензирования — с отметкой правки) или в новый.
   */
  private tocStyles(
    a: Extract<FixAction, { kind: 'toc' }>,
    levelIndent: number,
    styles: StyleSheet = this.a.model.styles,
  ): Partial<Record<number, string>> {
    const pkg = this.a.pkg;
    const path = pkg.relatedPart(this.a.model.mainPart, Rel.styles);
    const doc = path ? pkg.getXml(path) : undefined;
    const out: Partial<Record<number, string>> = {};
    for (let level = 1; level <= a.depth; level++) {
      const existing = styles.byName(`toc ${level}`);
      if (existing) out[level] = existing.id;
      if (!path || !doc) continue;
      let style = childElements(doc.root, 'w:style').find((s) => existing && s.attrs['w:styleId'] === existing.id);
      if (existing && !style) continue;
      let created = !!existing && this.createdStyles.has(existing.id);
      if (!style) {
        let id = `TOC${level}`;
        for (let n = 1; styles.get(id) || childElements(doc.root, 'w:style').some((s) => s.attrs['w:styleId'] === id); n++) id = `TOC${level}_${n}`;
        const base = styles.defaultParagraphStyle;
        style = el('w:style', { 'w:type': 'paragraph', 'w:styleId': id }, [
          el('w:name', { 'w:val': `toc ${level}` }),
          ...(base ? [el('w:basedOn', { 'w:val': base }), el('w:next', { 'w:val': base })] : []),
          el('w:uiPriority', { 'w:val': '39' }),
          el('w:unhideWhenUsed'),
        ]);
        appendChild(doc.root, style);
        this.createdStyles.add(id);
        created = true;
        out[level] = id;
      }
      const pPr = getOrCreate(style, 'w:pPr', STYLE_ORDER);
      const rPr = getOrCreate(style, 'w:rPr', STYLE_ORDER);
      if (this.mode === 'tracked' && !created) {
        if (!firstChild(pPr, 'w:pPrChange')) setOrdered(pPr, el('w:pPrChange', this.rev(), [snapshotProps(pPr, 'w:pPr', ['w:rPr', 'w:sectPr', 'w:pPrChange'])]), PPR_ORDER);
        if (!firstChild(rPr, 'w:rPrChange')) setOrdered(rPr, el('w:rPrChange', this.rev(), [snapshotProps(rPr, 'w:rPr', ['w:rPrChange'])]), RPR_ORDER);
      }
      for (const prop of tocParagraphProps(level, { tabPos: a.tabPos, levelIndent, line: a.line })) setOrdered(pPr, prop, PPR_ORDER);
      if (a.font) setOrdered(rPr, allFonts(a.font), RPR_ORDER);
      if (a.sz) {
        setOrdered(rPr, el('w:sz', { 'w:val': String(a.sz) }), RPR_ORDER);
        setOrdered(rPr, el('w:szCs', { 'w:val': String(a.sz) }), RPR_ORDER);
      }
      pkg.touch(path);
    }
    return out;
  }

  /** Удаление абзаца в режиме рецензирования: всё содержимое и знак абзаца — в w:del. */
  private trackDeleteParagraph(pEl: XElement): void {
    const pPr = ensurePPr(pEl);
    const rPr = getOrCreate(pPr, 'w:rPr', PPR_ORDER);
    setOrdered(rPr, el('w:del', this.rev()), PARA_RPR_ORDER);
    const wrap = (container: XElement): void => {
      container.children.forEach((c, i) => {
        if (c.type !== 'element') return;
        if (c.name === 'w:r') {
          for (const t of c.children) {
            if (t.type !== 'element') continue;
            if (t.name === 'w:t') t.name = 'w:delText';
            else if (t.name === 'w:instrText') t.name = 'w:delInstrText';
          }
          const del = el('w:del', this.rev());
          c.parent = del;
          del.children.push(c);
          del.parent = container;
          container.children[i] = del;
        } else if (['w:hyperlink', 'w:smartTag', 'w:customXml', 'w:fldSimple', 'w:ins'].includes(c.name)) {
          wrap(c);
        } else if (c.name === 'w:sdt') {
          const content = firstChild(c, 'w:sdtContent');
          if (content) wrap(content);
        }
      });
    };
    wrap(pEl);
  }
}

class CommentWriter {
  private doc?: XDocument;

  constructor(
    private readonly pkg: DocxPackage,
    private readonly mainPart: string,
    private readonly author: string,
    private readonly date: string,
    private readonly allocId: () => string,
  ) {}

  private ensure(): XDocument {
    if (this.doc) return this.doc;
    let path = this.pkg.relatedPart(this.mainPart, Rel.comments);
    let doc = path ? this.pkg.getXml(path) : undefined;
    if (path && doc) {
      this.pkg.touch(path);
    } else {
      path = 'word/comments.xml';
      for (let n = 1; this.pkg.has(path); n++) path = `word/comments${n}.xml`;
      doc = { prolog: XML_DECLARATION, root: el('w:comments', { 'xmlns:w': W_NS }), epilog: '' };
      this.pkg.putXml(path, doc);
      this.pkg.addRelationship(this.mainPart, COMMENTS_REL, relativeTarget(this.mainPart, path));
      this.pkg.setContentTypeOverride(path, COMMENTS_CT);
    }
    this.doc = doc;
    return doc;
  }

  add(pEl: XElement, lines: string[]): void {
    const doc = this.ensure();
    const id = this.allocId();
    const paras = lines.map((line, i) =>
      el('w:p', {}, [
        ...(i === 0 ? [el('w:r', {}, [el('w:annotationRef')])] : []),
        el('w:r', {}, [el('w:t', { 'xml:space': 'preserve' }, [textNode(line)])]),
      ]),
    );
    appendChild(doc.root, el('w:comment', { 'w:id': id, 'w:author': this.author, 'w:date': this.date, 'w:initials': 'ГОСТ' }, paras));
    const pPr = firstChild(pEl, 'w:pPr');
    insertAt(pEl, el('w:commentRangeStart', { 'w:id': id }), pPr ? pEl.children.indexOf(pPr) + 1 : 0);
    appendChild(pEl, el('w:commentRangeEnd', { 'w:id': id }));
    appendChild(pEl, el('w:r', {}, [el('w:commentReference', { 'w:id': id })]));
  }
}

const PHASE: Record<FixAction['kind'], number> = {
  text: 0,
  runs: 1,
  para: 2,
  section: 3,
  titlePage: 3,
  hfAlign: 3,
  updateFields: 3,
  meta: 3,
  pageNumbers: 3,
  bullet: 3,
  toc: 3,
};

export function fixDocument(bytes: Uint8Array, opts: FixOptions): FixResult {
  const profile = opts.profile ?? DEFAULT_PROFILE;
  const a = analyze(bytes, profile);
  const date = opts.date ?? new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
  const author = opts.author ?? 'Нормоконтроль gostword';
  const exclude = new Set(opts.exclude ?? []);
  const editor = new Editor(a, opts.mode === 'clean' ? 'clean' : 'tracked', author, date);

  const fixed = new Set<string>();
  let failed = 0;
  if (opts.mode !== 'none') {
    const jobs: Array<{ f: Finding; action: FixAction }> = [];
    for (const f of a.findings) {
      if (!f.fix?.length || exclude.has(f.id) || exclude.has(f.rule)) continue;
      for (const action of f.fix) jobs.push({ f, action });
    }
    // Текст правим с конца абзаца к началу, чтобы смещения оставались верными.
    jobs.sort((x, y) => {
      const px = PHASE[x.action.kind];
      const py = PHASE[y.action.kind];
      if (px !== py) return px - py;
      if (x.action.kind === 'text' && y.action.kind === 'text') {
        return x.action.paragraph - y.action.paragraph || y.action.start - x.action.start;
      }
      return 0;
    });
    const status = new Map<string, { ok: number; total: number }>();
    // Одно исправление может закрывать несколько замечаний (перенумерация, содержание) — применяем его один раз.
    const done = new Map<string, boolean>();
    for (const { f, action } of jobs) {
      const st = status.get(f.id) ?? { ok: 0, total: 0 };
      st.total++;
      const key = JSON.stringify(action);
      let ok = done.get(key);
      if (ok === undefined) {
        ok = editor.apply(action);
        done.set(key, ok);
      }
      if (ok) st.ok++;
      status.set(f.id, st);
    }
    editor.finishToc();
    for (const [id, st] of status) {
      if (st.ok === st.total) fixed.add(id);
      else failed++;
    }
  }

  let commented = 0;
  if (opts.comments) {
    const writer = new CommentWriter(a.pkg, a.model.mainPart, author, date, () => editor.allocId());
    const gost = new Map(ALL_RULES.map((r) => [r.id, forProfile(r.gost, profile.id)]));
    const line = (f: Finding): string => `• ${f.message}${gost.get(f.rule) ? ` (${gost.get(f.rule)})` : ''}`;
    const byPara = new Map<number, Finding[]>();
    const general: Finding[] = [];
    for (const f of a.findings) {
      if (fixed.has(f.id)) continue;
      const p = f.paragraph !== undefined ? a.model.paragraphs[f.paragraph] : undefined;
      if (p && !p.textBox) {
        const list = byPara.get(p.index) ?? [];
        list.push(f);
        byPara.set(p.index, list);
      } else general.push(f);
      commented++;
    }
    const anchor = a.model.paragraphs[a.structure.frontEnd] ?? a.model.paragraphs[0];
    if (general.length && anchor) writer.add(anchor.el, ['Замечания к документу в целом:', ...general.map(line)]);
    for (const [idx, list] of byPara) writer.add(a.model.paragraphs[idx]!.el, list.map(line));
  }

  a.pkg.touch(a.model.mainPart);
  return { bytes: a.pkg.toBytes(), fixed: fixed.size, commented, failed };
}
